import type { Procedure } from '../../../app-core/lib/procedure/Procedure'
import { E2E_dispatchOpenReportSaved } from '../E2E/E2E.dispatch'
import { IDB_openReports } from '../IDB/IDB.dexie'
import { IDB_procedureRead } from '../IDB/IDB.table.procedure'
import type { Rpts_ExhaustiveAnswers } from './Rpts.ExhaustiveAnswer'
import { type Rpts_ClientId, Rpts_createClientId } from './Rpts.types'
import { type Util_IISODateString, Util_isoDateNow } from '../Util/Util.date'
import type { UUID } from '../../../shared/lib/typeHelpers.no-deps'
import type { FlowUnitIds } from '../../../shared/domain/flows/FlowUnitsIds.no-deps'
import type {
    Type_ActivityWorkflow,
    Type_IActionType,
    Type_ICalendarEvent,
    Type_ILocation,
    Type_Workflow,
} from '../Type/Type.hive'

/**
 * There is only one open report at a time (at the moment)
 * The structure of the report (workflow and actionTypes) is cached, in case it changes whilst the report is open.
 * Also the workflow is used to store transitory information like which activities are locked
 * The calendar event, procedure and location is cached in case they get hard deleted whilst it's open
 */

export interface Rpts_OpenReport {
    clientId: Rpts_ClientId
    startDate: Util_IISODateString
    actionTypes: Type_IActionType[]
    calendarEvent: Type_ICalendarEvent
    location: Type_ILocation
    procedure: Procedure
    workflow: Type_ActivityWorkflow
    scheduledInstance?: UUID
    dueDate?: Util_IISODateString
    showOverdue?: boolean
    exhaustiveAnswers?: Rpts_ExhaustiveAnswers
    description?: string
    teamDoneFor?: number
    teams?: Array<{ id: number; name: string }>
    origin?: number
    isAction: boolean
    flowUnitIds: FlowUnitIds
    lat?: number
    lon?: number
}

/**
 * This is a hard coded key for the only row currently being used
 * Could have used the lookup table, but this leaves us open to migrate toward multiple open reports in the future
 */
const openReportKey = 'openReportKey'

export function Rpts_openReportFetch(): Promise<Rpts_OpenReport> {
    return IDB_openReports().get(openReportKey)
}

export async function Rpts_openReportWrite(openReport: Rpts_OpenReport) {
    await IDB_openReports().put(openReport, openReportKey)
    E2E_dispatchOpenReportSaved()
}

export async function Rpts_openReportClear() {
    await IDB_openReports().delete(openReportKey)
}

export async function Rpts_openReportUpdate(partial: Partial<Rpts_OpenReport>) {
    const current = await Rpts_openReportFetch()
    await Rpts_openReportWrite({ ...current, ...partial })
}

/**
 * The backups table is where the open report data used to be stored
 */
export type Rpts_LegacyBackups = Array<
    | { id: 'tasks'; options: Type_IActionType[] }
    | { id: 'activityEvent'; options: Type_ICalendarEvent }
    | { id: 'activityStore'; options: Type_ILocation }
    | { id: 'taskList'; options: Type_Workflow }
>

/**
 * Rpts.openReportRestoreFromBackups
 *
 * Helper for migrating from the legacy storage format
 */
export async function Rpts_openReportRestoreFromBackups(
    backups: Rpts_LegacyBackups,
    deps: {
        IDB_procedureRead: (id: number) => Promise<Procedure>
        Rpts_openReportWrite: (openReport: Rpts_OpenReport) => Promise<void>
        Util_isoDateNow: () => Util_IISODateString
        Rpts_createClientId: () => Rpts_ClientId
    } = {
        IDB_procedureRead,
        Rpts_openReportWrite,
        Util_isoDateNow,
        Rpts_createClientId,
    }
) {
    const findInBackups = (id: string) => {
        const row = backups.find((backup) => backup.id === id)
        return row != null ? row.options : null
    }

    const actionTypes = findInBackups('tasks') as Type_IActionType[]
    const calendarEvent = findInBackups('activityEvent') as Type_ICalendarEvent
    const location = findInBackups('activityStore') as Type_ILocation
    const workflow = findInBackups('taskList') as Type_ActivityWorkflow

    if (!actionTypes || !calendarEvent || !location || !workflow) return

    const procedure = await deps.IDB_procedureRead(calendarEvent.campaign)

    /**
     * This means that all open reports when migrated will have their start date
     * set to the time of migration which is later than desirable. I think this
     * is preferable to having null's and keeping the old way of inferring the start
     * date around
     */
    const startDate = deps.Util_isoDateNow()

    await deps.Rpts_openReportWrite({
        clientId: deps.Rpts_createClientId(),
        startDate,
        actionTypes,
        calendarEvent,
        location,
        procedure,
        workflow,
        exhaustiveAnswers: null,
        flowUnitIds: {},
        isAction: false,
    })
}
